/**
 * What changes from scenario A to scenario B (roadmap P-4): the assumptions
 * that differ, how the counts move, which priorities become achievable or
 * stop being achievable, and whether each is affordable. Display only; the
 * reading is `diffScenarioRuns`.
 */

import type { PriorityMove, ScenarioDiff } from '../../../lib/planningScenarioDiff';
import { DataTable } from '../../../components/ds';

const HEAD = 'text-xs font-semibold uppercase tracking-wider text-[var(--text-ter)] dark:text-[var(--text-sec)]';

function MoveList({ title, moves, tone }: { title: string; moves: PriorityMove[]; tone: string }) {
  if (moves.length === 0) return null;
  return (
    <div>
      <p className={HEAD}>
        {title} ({moves.length})
      </p>
      <ul className="mt-1 space-y-0.5 text-sm">
        {moves.map((m) => (
          <li key={m.priorityId} className="text-[var(--text-pri)]">
            <span className={tone}>●</span> {m.name}
            <span className="text-xs text-[var(--text-ter)]">
              {' '}
              — {m.a ?? 'not in run'} → {m.b ?? 'not in run'}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function ScenarioPairDiff({
  nameA,
  nameB,
  diff,
  affordabilityA,
  affordabilityB,
}: {
  nameA: string;
  nameB: string;
  diff: ScenarioDiff;
  affordabilityA: string;
  affordabilityB: string;
}) {
  const noFeasibilityChange =
    diff.gained.length === 0 && diff.lost.length === 0 && diff.otherChanges.length === 0;

  return (
    <div className="grid gap-4 md:grid-cols-2">
      <div className="space-y-3">
        <div>
          <p className={HEAD}>Assumptions that differ</p>
          {diff.assumptions.length === 0 ? (
            <p className="mt-1 text-sm text-[var(--text-sec)]">
              The scenario settings are the same; any difference comes from their capacity models.
            </p>
          ) : (
            <div className="mt-1">
              <DataTable
                density="compact"
                rule="horizontal"
                ariaLabel="Assumptions that differ"
                rows={diff.assumptions}
                getRowKey={(r) => r.label}
                columns={[
                  { key: 'label', header: '', cellClassName: 'text-[var(--text-sec)]' },
                  { key: 'a', header: nameA },
                  { key: 'b', header: nameB },
                ]}
              />
            </div>
          )}
        </div>

        {diff.counts.length > 0 && (
          <div>
            <p className={HEAD}>How the answer moves</p>
            <ul className="mt-1 text-sm space-y-0.5">
              {diff.counts.map((c) => {
                const delta = c.b - c.a;
                return (
                  <li key={c.label} className="text-[var(--text-pri)]">
                    {c.label}: <span className="tabular-nums">{c.a}</span> →{' '}
                    <span className="tabular-nums">{c.b}</span>
                    {delta !== 0 && (
                      <span className="text-xs text-[var(--text-ter)] tabular-nums">
                        {' '}
                        ({delta > 0 ? '+' : ''}
                        {delta})
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        <div>
          <p className={HEAD}>Funding</p>
          <ul className="mt-1 text-sm space-y-0.5 text-[var(--text-pri)]">
            <li>
              {nameA}: {affordabilityA}
            </li>
            <li>
              {nameB}: {affordabilityB}
            </li>
          </ul>
        </div>
      </div>

      <div className="space-y-3">
        {noFeasibilityChange ? (
          <p className="text-sm text-[var(--text-sec)]">
            Every priority has the same answer under both scenarios.
          </p>
        ) : (
          <>
            <MoveList title={`Achievable only under ${nameB}`} moves={diff.gained} tone="text-[var(--green)]" />
            <MoveList title={`Achievable only under ${nameA}`} moves={diff.lost} tone="text-[var(--coral)]" />
            <MoveList title="Other changes" moves={diff.otherChanges} tone="text-[var(--mustard)]" />
          </>
        )}
      </div>
    </div>
  );
}
